// BranchConnectedSection.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardMedia, CardContent, Button, Chip,
  Rating, Avatar, LinearProgress, Skeleton, useTheme, useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Star as StarIcon,
  Storefront as StorefrontIcon,
  LocationOn as LocationOnIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import { fetchBranchConnections, fetchBranchRatings } from './branchesApi';

const getImageUrl = (branch) => {
  return (
    branch.ImageUrl ||
    branch.imageUrl ||
    (typeof branch.Image === 'string' && branch.Image.startsWith('http') ? branch.Image : null) ||
    '/logo-storesye.png'
  );
};

const BranchConnectedSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedBranch, setSelectedBranch } = useCart();

  const [connectedBranches, setConnectedBranches] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [ratingStats, setRatingStats] = useState({ average: 0, total: 0, distribution: [0, 0, 0, 0, 0] });
  const [loading, setLoading] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    if (!selectedBranch) return;
    const branchId = selectedBranch.id || selectedBranch.Id;
    if (!branchId) return;

    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const [connections, ratingsData] = await Promise.all([
          fetchBranchConnections(branchId).catch(() => []),
          fetchBranchRatings(branchId, 1, 50).catch(() => ({ ratings: [], stats: { averageRating: 0, totalRatings: 0 } }))
        ]);

        if (!isMounted) return;

        setConnectedBranches(Array.isArray(connections) ? connections : []);

        const ratingsList = ratingsData?.ratings || [];
        setRatings(ratingsList);

        // Use backend stats if available, otherwise compute
        const dist = [0, 0, 0, 0, 0];
        let sum = 0;
        ratingsList.forEach(r => {
          const val = Number(r.Rating || r.rating || 0);
          if (val >= 1 && val <= 5) { dist[val - 1]++; sum += val; }
        });

        const avg = ratingsData?.stats?.averageRating
          ?? (ratingsList.length > 0 ? sum / ratingsList.length : 0);
        const total = ratingsData?.stats?.totalRatings ?? ratingsList.length;

        setRatingStats({ average: Number(avg) || 0, total, distribution: dist });
      } catch (e) {
        console.log('BranchConnectedSection error:', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [selectedBranch?.id, selectedBranch?.Id]);

  const handleSelectBranch = useCallback((branch) => {
    // Normalize the connected branch payload into selectedBranch shape
    const normalized = {
      id: branch.Id || branch.id,
      Id: branch.Id || branch.id,
      name: branch.Name || branch.name,
      Name: branch.Name || branch.name,
      address: branch.Address || branch.address,
      Address: branch.Address || branch.address,
      ImageUrl: branch.ImageUrl || branch.imageUrl,
      imageUrl: branch.ImageUrl || branch.imageUrl,
      Image: branch.Image || branch.image,
      isActive: true,
      IsActive: true,
      isFeatured: branch.IsFeatured ?? branch.isFeatured ?? false,
      IsFeatured: branch.IsFeatured ?? branch.isFeatured ?? false,
      averageRating: branch.AverageRating ?? 0,
      AverageRating: branch.AverageRating ?? 0,
      totalRatings: branch.TotalRatings ?? 0,
      TotalRatings: branch.TotalRatings ?? 0,
      totalLikes: branch.TotalLikes ?? 0,
      TotalLikes: branch.TotalLikes ?? 0,
      ...branch,
    };

    setSelectedBranch(normalized);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Reload to fetch fresh products/categories for new branch
    setTimeout(() => window.location.reload(), 100);
  }, [setSelectedBranch, navigate]);

  const displayedRatings = useMemo(
    () => showAllReviews ? ratings : ratings.slice(0, 3),
    [ratings, showAllReviews]
  );

  if (!selectedBranch) return null;

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  // Don't render if no connections AND no reviews — keep UI clean
  if (connectedBranches.length === 0 && ratings.length === 0 && ratingStats.total === 0) {
    return null;
  }

  return (
    <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>

      {/* ═══════════ CONNECTED BRANCHES ═══════════ */}
      {connectedBranches.length > 0 && (
        <Box sx={{
          mb: { xs: 3, sm: 4 },
          p: { xs: 2, sm: 3 },
          bgcolor: 'background.paper',
          borderRadius: { xs: 2, sm: 3 },
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.04)}`
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: { xs: 2, sm: 2.5 },
            pb: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}>
            <StorefrontIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' } }}>
                {t('home.connectedStores', 'Connected Stores')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {t('home.connectedStoresSubtitle', 'Branches linked to this store')}
              </Typography>
            </Box>
          </Box>

          {/* Horizontal Cards */}
          <Box sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { height: 5 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              borderRadius: 3
            }
          }}>
            {connectedBranches.map(branch => {
              const img = getImageUrl(branch);
              const rating = Number(branch.AverageRating || 0);
              const totalLikes = Number(branch.TotalLikes || 0);
              const totalRatings = Number(branch.TotalRatings || 0);
              const isFeatured = branch.IsFeatured;
              const branchId = branch.Id || branch.id;

              return (
                <Card
                  key={branchId}
                  onClick={() => handleSelectBranch(branch)}
                  sx={{
                    minWidth: { xs: 160, sm: 190, md: 220 },
                    maxWidth: { xs: 160, sm: 190, md: 220 },
                    cursor: 'pointer',
                    borderRadius: { xs: 2, sm: 2.5 },
                    overflow: 'hidden',
                    flexShrink: 0,
                    border: isFeatured
                      ? '2px solid #F59E0B'
                      : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  {/* Image */}
                  <Box sx={{ position: 'relative', width: '100%', paddingTop: '70%', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={img}
                      alt={branch.Name || branch.name}
                      loading="lazy"
                      sx={{
                        position: 'absolute', top: 0, left: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    />
                    {isFeatured && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 10, color: 'white !important' }} />}
                        label={t('branch.featured', 'Featured')}
                        size="small"
                        sx={{
                          position: 'absolute', top: 6, left: 6,
                          bgcolor: '#F59E0B', color: 'white',
                          fontWeight: 700, fontSize: '0.6rem', height: 20,
                          '& .MuiChip-icon': { ml: 0.3 }
                        }}
                      />
                    )}
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
                      height: '40%'
                    }} />
                  </Box>

                  <CardContent sx={{ p: { xs: 1.2, sm: 1.5 } }}>
                    <Typography sx={{
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      fontWeight: 700, mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {branch.Name || branch.name}
                    </Typography>

                    {rating > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Rating value={rating} precision={0.5} readOnly size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
                        <Typography sx={{ fontSize: '0.65rem', color: '#F59E0B', fontWeight: 700 }}>
                          {rating.toFixed(1)}
                        </Typography>
                        {totalRatings > 0 && (
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                            ({totalRatings})
                          </Typography>
                        )}
                      </Box>
                    )}

                    {(branch.Address || branch.address) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 10, color: 'text.secondary' }} />
                        <Typography sx={{
                          fontSize: { xs: '0.6rem', sm: '0.7rem' },
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {branch.Address || branch.address}
                        </Typography>
                      </Box>
                    )}

                    {totalLikes > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.8 }}>
                        <ThumbUpIcon sx={{ fontSize: 10, color: '#10B981' }} />
                        <Typography sx={{ fontSize: '0.6rem', color: '#10B981', fontWeight: 600 }}>
                          {totalLikes}
                        </Typography>
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      endIcon={<ArrowForwardIosIcon sx={{ fontSize: '10px !important' }} />}
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        fontWeight: 700, borderRadius: 2, py: 0.6,
                        textTransform: 'none',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }}
                    >
                      {t('branch.visitStore', 'Visit Store')}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ═══════════ RATINGS & REVIEWS ═══════════ */}
      {(ratingStats.total > 0 || ratings.length > 0) && (
        <Box sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: 'background.paper',
          borderRadius: { xs: 2, sm: 3 },
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.04)}`
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            mb: { xs: 2, sm: 2.5 }, pb: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}>
            <ChatBubbleOutlineIcon sx={{ color: 'primary.main', fontSize: { xs: 22, sm: 26 } }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' } }}>
                {t('home.customerReviews', 'Customer Reviews')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {t('home.customerReviewsSubtitle', 'What customers say about us')}
              </Typography>
            </Box>
          </Box>

          {/* Rating Summary */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            p: { xs: 1.5, sm: 2 },
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: { xs: 'auto', sm: 120 } }}>
              <Typography sx={{ fontSize: { xs: '2.5rem', sm: '3rem' }, fontWeight: 900, color: 'primary.main', lineHeight: 1 }}>
                {ratingStats.average.toFixed(1)}
              </Typography>
              <Rating value={ratingStats.average} precision={0.5} readOnly size={isMobile ? 'small' : 'medium'} sx={{ my: 0.5 }} />
              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, color: 'text.secondary' }}>
                {ratingStats.total} {ratingStats.total === 1 ? t('home.review', 'review') : t('home.reviews', 'reviews')}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
              {[5, 4, 3, 2, 1].map(star => {
                const count = ratingStats.distribution[star - 1];
                const pct = ratingStats.total > 0 ? (count / ratingStats.total) * 100 : 0;
                return (
                  <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, width: 12, textAlign: 'center' }}>{star}</Typography>
                    <StarIcon sx={{ fontSize: 14, color: '#F59E0B' }} />
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 8, borderRadius: 4,
                          bgcolor: alpha(theme.palette.divider, 0.15),
                          '& .MuiLinearProgress-bar': { bgcolor: '#F59E0B', borderRadius: 4 }
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, width: 24, textAlign: 'right' }}>{count}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Reviews */}
          {ratings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 5 } }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: { xs: 48, sm: 56 }, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                {t('home.noReviewsYet', 'No reviews yet')}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, color: 'text.disabled' }}>
                {t('home.beTheFirstToReview', 'Be the first to rate this store!')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                {displayedRatings.map((r, idx) => {
                  const userName = r.UserName || r.userName || t('home.anonymousUser', 'Anonymous');
                  const ratingVal = Number(r.Rating || r.rating || 0);
                  const comment = r.Comment || r.comment || '';
                  const date = r.CreatedAt || r.createdAt;
                  const formattedDate = date ? new Date(date).toLocaleDateString() : '';
                  const initial = (userName.charAt(0) || '?').toUpperCase();

                  return (
                    <Box
                      key={r.Id || r.id || idx}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: comment ? 1 : 0 }}>
                        <Avatar sx={{
                          width: { xs: 36, sm: 42 }, height: { xs: 36, sm: 42 },
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 800,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}>
                          {initial}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                            {userName}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                            <Rating value={ratingVal} precision={1} readOnly size="small" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }} />
                            {formattedDate && (
                              <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: 'text.disabled' }}>
                                • {formattedDate}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {comment && (
                        <Typography sx={{
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          pl: { xs: 6, sm: 7 }
                        }}>
                          {comment}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {ratings.length > 3 && (
                <Button
                  fullWidth
                  onClick={() => setShowAllReviews(prev => !prev)}
                  endIcon={showAllReviews ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    mt: 2,
                    py: { xs: 1, sm: 1.2 },
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: 'primary.main',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  {showAllReviews
                    ? t('home.showLess', 'Show Less')
                    : `${t('home.viewAllReviews', 'View All Reviews')} (${ratings.length})`
                  }
                </Button>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BranchConnectedSection;